import { DiscountCalculationType, DiscountRestriction, ItemPriceRestriction, ItemRestriction } from '@vista-digital/ocapi-types/v1';
import { logger } from '../../../../utils/logger';
import { compareMonetaryValue } from '../../../../utils/monetary-value-utils';
import { isMemberSignedIn } from '../../../loyalty/selectors/member/is-member-signed-in';
import { getAvailableMemberRewardCount } from '../../../loyalty/utils/member-reward-utils';
import { getDiscountById } from '../../selectors/discounts/get-discount-by-id';
import { getDefaultItemPrice } from './get-default-item-price';
import { getAvailableMemberRewardCountByDiscount } from './item-discount-utils';
export const isNormalItemAvailable = (item, prices) => {
    const isAvailable = item.restrictions.every(restriction => {
        switch (restriction) {
            case ItemRestriction.Member:
                return isMemberSignedIn();
            default:
                logger.warn(`Cannot sell item '${item.id}' with unknown restriction '${restriction}'.`);
                return false;
        }
    });
    if (!isAvailable)
        return false;
    const defaultPrice = getDefaultItemPrice(prices);
    return isItemPriceAvailable(defaultPrice);
};
const isItemPriceAvailable = (price) => {
    const exclusiveRestrictions = price.restrictions.filter(isRestrictionExclusive);
    if (exclusiveRestrictions.length > 1)
        return false;
    return price.restrictions.every(restriction => {
        switch (restriction) {
            case ItemPriceRestriction.MemberReward:
                return isMemberRewardPriceAvailable(price.rewardId);
            case ItemPriceRestriction.Discount:
                return isDiscountPriceAvailable(price);
            default:
                logger.warn(`Cannot sell item '${price}' with unknown restriction '${restriction}'.`);
                return false;
        }
    });
};
/**
 * We do not support combinations of some restrictions, so we consider them to be exclusive.
 * If a item price has multiple exclusive restrictions, we filter it out.
 */
const isRestrictionExclusive = (restriction) => {
    switch (restriction) {
        case ItemPriceRestriction.Discount:
        case ItemPriceRestriction.InheritedDiscount:
        case ItemPriceRestriction.MemberReward:
        case ItemPriceRestriction.InheritedMemberReward:
            return true;
        default:
            return false;
    }
};
const isMemberRewardPriceAvailable = (rewardId) => !!getAvailableMemberRewardCount(rewardId);
export const getLowestItemPrice = (itemPrices) => {
    const availableItemPrices = itemPrices.filter(price => isItemPriceAvailable(price));
    if (!availableItemPrices.length) {
        logger.error('No prices are available');
        return;
    }
    const lowestPrice = availableItemPrices.sort((a, b) => compareMonetaryValue(a.price, b.price))[0];
    return lowestPrice;
};
export const isDiscountPriceAvailable = (price) => {
    const discount = getDiscountById(price.discountId);
    if (!discount)
        return false;
    switch (discount.restriction) {
        case DiscountRestriction.MemberReward:
            return !!getAvailableMemberRewardCountByDiscount(discount);
        case DiscountRestriction.Voucher:
            return false;
        case DiscountRestriction.None:
            return true;
        default:
            logger.warn(`Cannot evaluate discount for unknown discount '${discount}'.`);
            return false;
    }
};
export const isInheritedDiscountPriceAvailable = (price, baseItem) => {
    const lowestItemPriceForBaseItem = getLowestItemPrice(baseItem.prices);
    if (!lowestItemPriceForBaseItem || !isPercentageOffDiscountPrice(lowestItemPriceForBaseItem))
        return false;
    return lowestItemPriceForBaseItem.discountId === price.discountId;
};
export const isInheritedMemberRewardPriceAvailable = (price, baseItem) => {
    const lowestItemPriceForBaseItem = getLowestItemPrice(baseItem.prices);
    if (!lowestItemPriceForBaseItem || !isMemberRewardItemPrice(lowestItemPriceForBaseItem))
        return false;
    return lowestItemPriceForBaseItem.rewardId === price.rewardId;
};
const isPercentageOffDiscountPrice = (price) => isDiscountItemPrice(price) && price.discount.calculation.type === DiscountCalculationType.PercentageOff;
export const isDiscountOrMemberRewardItemPrice = (price) => isDiscountItemPrice(price) || isMemberRewardItemPrice(price);
export const isMemberRewardItemPrice = (price) => {
    return price.restrictions.includes(ItemPriceRestriction.MemberReward);
};
export const isInheritedMemberRewardItemPrice = (price) => {
    return price.restrictions.includes(ItemPriceRestriction.InheritedMemberReward);
};
export const isDiscountItemPrice = (price) => {
    return price.restrictions.includes(ItemPriceRestriction.Discount);
};
export const isInheritedDiscountItemPrice = (price) => {
    return price.restrictions.includes(ItemPriceRestriction.InheritedDiscount);
};
export const isInheritedItemPrice = (price) => {
    return isInheritedMemberRewardItemPrice(price) || isInheritedDiscountItemPrice(price);
};
